import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { ModalBox } from "./modal-box.component";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { useWhiteLabelContext } from "../../../WhiteLabelContext";
import { useApiClient } from "../../../data/clients/ApiClient";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useOrganizationImpersonationContext } from "../../../contexts/OrganizationImpersonationContext";
import { Role } from "../../../core/models/entities/Role";
import { enviroment } from "../../../enviroment";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from "../Toast/Toast";
import {
  classifySheetSizeRequestOutcome,
  formatCm,
  isSheetSizeRequestNotFound,
  OVERSIZE_SHEET_MAX_REQUESTS,
  OVERSIZE_SHEET_REQUEST_TIMEOUT_MS,
  OversizeLogoInfo,
  OversizeSheetContext,
  RequestedMap,
  resolveSheetSizeRequestOrgId,
  resolveSheetSizeRequestRetryOrgId,
  SheetSizeRequestOutcome,
} from "../../../utils/functions/oversizeSheet";
import {
  closeOversizeSheetDialog,
  hideOversizeSheetRequest,
  markSheetSizeRequested,
  useOversizeSheetDialogState,
  useOversizeSheetViewer,
} from "../../../hooks/useOversizeSheetGuard";

const DEFAULT_COLOR_HEX = "#7BBB58";

export interface OversizeSheetDialogProps {
  open: boolean;
  context: OversizeSheetContext;
  logos: OversizeLogoInfo[];
  requested: RequestedMap;
  hideRequest: boolean;
  backToCart: boolean;
  requesting: boolean;
  colorHex?: string;
  onRequest: () => void;
  onClose: () => void;
  onBackToCart?: () => void;
}

const BODY_KEY: Record<OversizeSheetContext, string> = {
  add: "oversizeSheet.bodyAdd",
  reorder: "oversizeSheet.bodyReorder",
  place: "oversizeSheet.bodyPlace",
};

/**
 * Flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED: shown when an image logo's
 * sheet size is no longer available - at add-to-cart (the logo was NOT
 * added), at reorder (the offending lines were left out) or when the API
 * refused CommitOrder/PlaceOrder with 409 sheetSizeUnavailable (context
 * "place"). "Request new sheet size" = one call per listed logo; "Cancel"
 * becomes "Close" once a request was sent; "Back to cart" only on the
 * order details page, which cannot remove lines itself.
 */
export function OversizeSheetDialog({
  open,
  context,
  logos,
  requested,
  hideRequest,
  backToCart,
  requesting,
  colorHex,
  onRequest,
  onClose,
  onBackToCart,
}: OversizeSheetDialogProps) {
  const { t, i18n } = useTranslation();
  const color = colorHex || DEFAULT_COLOR_HEX;
  const locale = i18n?.language || undefined;
  const isRequested = (logoId: number) => requested[logoId] !== undefined;
  const anyRequested = logos.some((l) => isRequested(l.logoId));
  const allRequested = logos.length > 0 && logos.every((l) => isRequested(l.logoId));

  return (
    <Modal
      aria-labelledby="oversize-sheet-title"
      aria-describedby="oversize-sheet-body"
      open={open}
      onClose={onClose}
    >
      <ModalBox
        // "!" on both: ModalBox's own w-fit / min-w-[400px] have the same
        // specificity and come later in the built CSS (pdf-not-sharp-dialog).
        className="relative !min-w-0 !w-[92vw] max-w-[480px]"
        onClick={(e) => e.stopPropagation()}
        data-testid="oversize-sheet-dialog"
        style={
          {
            "--primary": color,
            "--primary-bg": `${color}10`,
          } as React.CSSProperties
        }
      >
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon
              size="2x"
              icon={faTriangleExclamation}
              className="text-[#E8A33D]"
            />
            <h2 id="oversize-sheet-title" className="text-lg font-semibold">
              {t("oversizeSheet.title")}
            </h2>
          </div>
          <p id="oversize-sheet-body" className="text-sm">
            {t(BODY_KEY[context])}
          </p>
          <ul className="text-sm list-disc pl-5 space-y-1 max-h-[30vh] overflow-y-auto">
            {logos.map((logo) => (
              <li key={logo.logoId} data-testid="oversize-sheet-logo">
                <span>
                  {t("oversizeSheet.logoLine", {
                    title: logo.title,
                    serial: logo.serial,
                    widthCm: formatCm(logo.widthMm, locale),
                    heightCm: formatCm(logo.heightMm, locale),
                  })}
                </span>
                {isRequested(logo.logoId) ? (
                  <span className="ml-2 text-xs text-gray-500">
                    {t("oversizeSheet.requested")}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <SecondaryButton width="fit" className="px-4" onClick={onClose}>
              {anyRequested ? t("oversizeSheet.close") : t("oversizeSheet.cancel")}
            </SecondaryButton>
            {backToCart && onBackToCart ? (
              <SecondaryButton width="fit" className="px-4" onClick={onBackToCart}>
                {t("oversizeSheet.backToCart")}
              </SecondaryButton>
            ) : null}
            {hideRequest ? null : (
              <PrimaryButton
                width="fit"
                className="px-4"
                disabled={allRequested || requesting}
                onClick={onRequest}
              >
                {allRequested ? t("oversizeSheet.requested") : t("oversizeSheet.request")}
              </PrimaryButton>
            )}
          </div>
        </div>
      </ModalBox>
    </Modal>
  );
}

const OUTCOME_TOAST_KEY: Record<SheetSizeRequestOutcome, string> = {
  sent: "oversizeSheet.requestSent",
  alreadyRequested: "oversizeSheet.alreadyRequested",
  dailyCapReached: "oversizeSheet.tryLater",
  notConfigured: "oversizeSheet.notConfigured",
  notApplicable: "oversizeSheet.notApplicable",
  requestFailed: "oversizeSheet.requestFailed",
};

/** One request call's outcome before it is sorted into a toast. */
type SheetSizeRequestAttempt =
  | { ok: true; result: { sent?: unknown; reason?: unknown } | null | undefined }
  | { ok: false; error: unknown };

/**
 * The ONE host of the dialog (App.tsx, inside CartProvider): renders null
 * while the flag is off or the store is closed, otherwise the dialog bound
 * to the module store, and sends the request calls - one per logo not yet
 * requested, sequentially, at most OVERSIZE_SHEET_MAX_REQUESTS, each under
 * an 8 s abort, with { designId } as the body when the logo was refused as
 * part of a design. The organization of the call is the session's org (act-as
 * org, else the profile's org), see resolveSheetSizeRequestOrgId; a 404
 * under it gets ONE retry under the logo's own org when that is a
 * different org (a shared / parent-customer / design-fallback logo the
 * session can see but that is not linked to its org, see
 * resolveSheetSizeRequestRetryOrgId) - a 404 there too is "not
 * configured", as before. An admin WITHOUT act-as posts under the logo's
 * own org (a global logo under the profile org, never organizations/0);
 * an admin acting as an org has that org's role from /profile/{orgId} and
 * follows the session rule like everyone else.
 * Closing the dialog (Cancel/Close, backdrop, Back to cart, unmount) aborts
 * the running loop: the in-flight call is cancelled, the remaining logos
 * are not sent and no toast is shown after the close - a 202 that had
 * already arrived is still remembered as "Request sent".
 */
export function OversizeSheetDialogHost() {
  const state = useOversizeSheetDialogState();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { whiteLabel } = useWhiteLabelContext();
  const { requestSheetSize } = useApiClient();
  const { organizationId: profileOrgId, role } = useProfileContext();
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();
  const isAdmin = role === Role.Admin;
  useOversizeSheetViewer();
  const [requesting, setRequesting] = useState(false);
  const inFlight = useRef(false);
  // The running request loop's controller; aborted on close and unmount.
  const loopAbort = useRef<AbortController | null>(null);
  const abortLoop = () => {
    loopAbort.current?.abort();
    loopAbort.current = null;
  };
  useEffect(() => {
    if (!state.open) abortLoop();
  }, [state.open]);
  // A new logo set (the dialog re-opened over a running loop) is new dialog
  // content: the loop for the previous set must not finish or toast for it.
  // The store keeps the array identity for every other update.
  useEffect(() => {
    abortLoop();
  }, [state.logos]);
  useEffect(() => abortLoop, []);

  if (!enviroment.oversizeSheetGuardEnabled || !state.open) return null;

  // One POST under `orgId`, under the 8 s abort and the loop's abort.
  const postOne = async (
    orgId: number,
    logo: OversizeLogoInfo,
    loopSignal: AbortSignal
  ): Promise<SheetSizeRequestAttempt> => {
    const controller = new AbortController();
    const onLoopAbort = () => controller.abort();
    if (loopSignal.aborted) controller.abort();
    else loopSignal.addEventListener("abort", onLoopAbort);
    const timer = setTimeout(() => controller.abort(), OVERSIZE_SHEET_REQUEST_TIMEOUT_MS);
    try {
      const result = await requestSheetSize(
        orgId,
        logo.logoId,
        controller.signal,
        logo.designId !== undefined && logo.designId > 0
          ? { designId: logo.designId }
          : undefined
      );
      return { ok: true, result };
    } catch (error) {
      return { ok: false, error };
    } finally {
      clearTimeout(timer);
      loopSignal.removeEventListener("abort", onLoopAbort);
    }
  };

  const sendOne = async (
    logo: OversizeLogoInfo,
    loopSignal: AbortSignal
  ): Promise<SheetSizeRequestOutcome> => {
    const orgId = resolveSheetSizeRequestOrgId(logo.orgId, actAsOrgId, profileOrgId, isAdmin);
    if (!(orgId > 0)) return "requestFailed";
    let attempt = await postOne(orgId, logo, loopSignal);
    // 404 under the session's org: the logo may be one the session can see
    // without being linked to its org (sharing relation, parent customer,
    // design fallback) - one retry under the logo's own org, error path only.
    const retryOrgId = resolveSheetSizeRequestRetryOrgId(orgId, logo.orgId, isAdmin);
    if (
      retryOrgId > 0 &&
      !loopSignal.aborted &&
      !attempt.ok &&
      isSheetSizeRequestNotFound(attempt.error)
    ) {
      attempt = await postOne(retryOrgId, logo, loopSignal);
    }
    return attempt.ok
      ? classifySheetSizeRequestOutcome(attempt.result, undefined)
      : classifySheetSizeRequestOutcome(undefined, attempt.error);
  };

  const onRequest = async () => {
    if (inFlight.current) return;
    const pending = state.logos
      .filter((l) => state.requested[l.logoId] === undefined)
      .slice(0, OVERSIZE_SHEET_MAX_REQUESTS);
    if (pending.length === 0) return;
    inFlight.current = true;
    setRequesting(true);
    const loop = new AbortController();
    loopAbort.current = loop;
    try {
      for (const logo of pending) {
        if (loop.signal.aborted) break;
        const outcome = await sendOne(logo, loop.signal);
        if (loop.signal.aborted) {
          // Closed while the call was out: keep the fact when the API did
          // send it, but no toast and no further calls.
          if (outcome === "sent" || outcome === "alreadyRequested") {
            markSheetSizeRequested(logo.logoId);
          }
          break;
        }
        const text = t(OUTCOME_TOAST_KEY[outcome]);
        switch (outcome) {
          case "sent":
            markSheetSizeRequested(logo.logoId);
            showSuccessToast(text);
            break;
          case "alreadyRequested":
            markSheetSizeRequested(logo.logoId);
            showInfoToast(text);
            break;
          case "dailyCapReached":
            showWarningToast(text);
            break;
          case "notConfigured":
            hideOversizeSheetRequest();
            showInfoToast(text);
            break;
          case "notApplicable":
            showInfoToast(text);
            break;
          default:
            showErrorToast(text);
            break;
        }
        // Requests are not configured: the remaining calls would answer the same.
        if (outcome === "notConfigured") break;
      }
    } finally {
      if (loopAbort.current === loop) loopAbort.current = null;
      inFlight.current = false;
      setRequesting(false);
    }
  };

  const onClose = () => {
    abortLoop();
    closeOversizeSheetDialog();
  };

  const onBackToCart = () => {
    abortLoop();
    closeOversizeSheetDialog();
    navigate("/order");
  };

  return (
    <OversizeSheetDialog
      open={state.open}
      context={state.context}
      logos={state.logos}
      requested={state.requested}
      hideRequest={state.hideRequest}
      backToCart={state.backToCart}
      requesting={requesting}
      colorHex={whiteLabel?.colorHex}
      onRequest={onRequest}
      onClose={onClose}
      onBackToCart={state.backToCart ? onBackToCart : undefined}
    />
  );
}
